import { useState, useEffect } from "react";
import { message } from "antd";
import { IndoorPositioning } from "../../../../api";
import { MIN_DIMENSION } from "../utils/constants";

const NO_GEO = {
  geoEnabled: false,
  geoOriginLat: null,
  geoOriginLng: null,
  geoRotationDeg: 0,
  geoTileLayers: [],
};

// DECIMAL columns can arrive as strings.
const num = (v) => (v === null || v === undefined ? null : Number(v));

export const useFloorPlanData = (machineID, open = true) => {
  const [dimensions, setDimensions] = useState({ width: 1, height: 1 });
  // The size 保存 last wrote (or the load read): what a size change is against.
  const [savedDimensions, setSavedDimensions] = useState(null);
  const [layoutType, setLayoutType] = useState("plane");
  const [imageUrl, setImageUrl] = useState(null);
  const [boundaryArea, setBoundaryArea] = useState(null);
  const [geo, setGeo] = useState(NO_GEO);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadFloorPlan = async () => {
    if (!machineID) return;

    setLoading(true);
    try {
      const response = await IndoorPositioning.GetIndoorPositioning(machineID);
      if (response.code === 200 && response.data) {
        const loaded = {
          width: Math.max(
            response.data.floor_width || MIN_DIMENSION,
            MIN_DIMENSION,
          ),
          height: Math.max(
            response.data.floor_height || MIN_DIMENSION,
            MIN_DIMENSION,
          ),
        };
        setDimensions(loaded);
        setSavedDimensions(loaded);
        setImageUrl(response.data.floor_image_url || null);
        setBoundaryArea(
          Array.isArray(response.data.boundary_area)
            ? response.data.boundary_area
            : null,
        );
        setGeo({
          geoEnabled: !!response.data.geo_enabled,
          geoOriginLat: num(response.data.geo_origin_lat),
          geoOriginLng: num(response.data.geo_origin_lng),
          geoRotationDeg: num(response.data.geo_rotation_deg) ?? 0,
          geoTileLayers: Array.isArray(response.data.geo_tile_layers)
            ? response.data.geo_tile_layers
            : [],
        });
        if (response.data.layout_type) {
          setLayoutType(response.data.layout_type);
        } else {
          setLayoutType("plane");
        }
      } else {
        setDimensions({ width: MIN_DIMENSION, height: MIN_DIMENSION });
        setSavedDimensions(null);
        setImageUrl(null);
        setBoundaryArea(null);
        setGeo(NO_GEO);
      }
    } catch (error) {
      console.error("載入平面圖設定失敗:", error);
      message.error("載入平面圖設定失敗");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!machineID) {
      message.error("機器ID不存在");
      return false;
    }

    setUploading(true);
    try {
      const response = await IndoorPositioning.UploadFloorImage(
        file,
        machineID,
      );
      if (response.code === 200) {
        setImageUrl(response.data.floor_image_url);
        message.success("圖片上傳成功");
      } else {
        message.error(response.message || "圖片上傳失敗");
      }
    } catch (error) {
      console.error("圖片上傳失敗:", error);
      message.error("圖片上傳失敗");
    } finally {
      setUploading(false);
    }

    return false;
  };

  const handleRemoveImage = async () => {
    if (!machineID) {
      setImageUrl(null);
      return;
    }

    try {
      const response = await IndoorPositioning.DeleteFloorImage(machineID);
      if (response.code === 200) {
        setImageUrl(null);
        message.success("已移除圖片");
      } else {
        message.error(response.message || "移除圖片失敗");
      }
    } catch (error) {
      console.error("移除圖片失敗:", error);
      message.error("移除圖片失敗");
    }
  };

  useEffect(() => {
    if (open) {
      loadFloorPlan();
    }
  }, [open, machineID]);

  return {
    dimensions,
    setDimensions,
    savedDimensions,
    setSavedDimensions,
    layoutType,
    setLayoutType,
    imageUrl,
    setImageUrl,
    boundaryArea,
    setBoundaryArea,
    ...geo,
    loading,
    uploading,
    loadFloorPlan,
    handleUpload,
    handleRemoveImage,
  };
};
